import { createPartialPlumaTheme } from '@customerio/pluma-components/css/partial-theme';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

const { color, palette } = themeVars;

const SCOPE_SLOTS = [
  'border',
  'mark',
  'text',
  'solid-bg',
  'solid-text',
  'solid-mark',
  'subtle-bgMix',
  'subtle-border',
  'subtle-text',
  'subtle-mark',
] as const;

type RepointedScope = 'accent' | 'information' | 'feature' | 'success';
type ScopeHue = 'orange' | 'violet' | 'fuchsia' | 'lime';

/** Points every slot of a semantic scope at the same slot of a hue scope. */
function repointScope(scope: RepointedScope, hue: ScopeHue) {
  return Object.fromEntries(SCOPE_SLOTS.map((slot) => [`scope-${scope}-${slot}`, color[`scope-${hue}-${slot}`]]));
}

/**
 * Halloween over Pluma's dark theme: violet-tinted surfaces, orange text and actions, a lime
 * focus ring. Caution, critical and destructive stay stock on purpose; every token not listed
 * here is dark's.
 */
export const halloweenPartialTheme = createPartialPlumaTheme({
  name: 'halloween-dark',
  colorScheme: 'dark',
  tokens: {
    color: {
      'surface-base': `color-mix(in oklab, ${palette['violet-1000']} 50%, ${palette['gray-1000']})`,
      'surface-recessed': `color-mix(in oklab, ${palette['violet-1000']} 50%, ${palette['gray-1000']})`,
      'surface-content': `color-mix(in oklab, ${palette['violet-950']} 35%, ${palette['gray-975']})`,
      'surface-block': `color-mix(in oklab, ${palette['violet-950']} 35%, ${palette['gray-975']})`,
      'surface-floating': `color-mix(in oklab, ${palette['violet-950']} 50%, ${palette['gray-950']})`,
      'surface-inverse': palette['orange-100'],
      'surface-track': palette['violet-700'],
      'text-primary': palette['orange-50'],
      'text-secondary': palette['violet-300'],
      'text-disabled': palette['violet-500'],
      'text-inverse': palette['violet-1000'],
      'text-code': palette['lime-300'],
      'mark-primary': palette['orange-50'],
      'mark-secondary': palette['violet-400'],
      'mark-disabled': palette['violet-500'],
      'scope-neutral-border': palette['violet-800'],
      'scope-neutral-subtle-border': `color-mix(in oklab, ${palette['violet-900']} 40%, ${palette['gray-925']})`,
      'scope-control-bg': color['surface-content'],
      'scope-control-border': palette['violet-500'],
      'scope-control-solid-bg': palette['orange-500'],
      'scope-control-solid-mark': palette['violet-1000'],
      'scope-control-subtle-border': `color-mix(in oklab, ${palette['violet-900']} 40%, ${palette['gray-900']})`,
      'scope-action-solid-bg': palette['orange-500'],
      'scope-action-solid-text': palette['violet-1000'],
      'scope-action-solid-mark': palette['violet-1000'],
      'scope-action-text': palette['orange-400'],
      'scope-focus-border': palette['lime-400'],
      ...repointScope('accent', 'orange'),
      ...repointScope('information', 'violet'),
      ...repointScope('feature', 'fuchsia'),
      ...repointScope('success', 'lime'),
    },
    colorMix: {
      'backdrop-color': palette['violet-1000'],
      'fillSubtle-withColor': color['surface-content'],
      'overlay-color': palette['orange-100'],
      'overlay-inverse-color': palette['violet-1000'],
    },
  },
});
